/**
 * The shader programs, each a vertex and a fragment shader assembled from
 * shared chunks and its own GLSL file. Every shader is GLSL ES 1.00, so the
 * same source runs on WebGL 1 and 2.
 *
 * The chunks:
 *   vertexHead    precision
 *   fragmentHead  precision, and the dither that hides 8-bit banding
 *   common        screen to clip space, the pixel ratio, an easing curve
 *   camera        the globe's camera, the backlight's daylight, the shade
 *                 and the boost round active places
 */
import camera from './shaders/camera.glsl?raw'
import common from './shaders/common.glsl?raw'
import coastFrag from './shaders/coast.frag.glsl?raw'
import coastVert from './shaders/coast.vert.glsl?raw'
import discFrag from './shaders/disc.frag.glsl?raw'
import discVert from './shaders/disc.vert.glsl?raw'
import fragmentHead from './shaders/fragment-head.glsl?raw'
import grainFrag from './shaders/grain.frag.glsl?raw'
import grainVert from './shaders/grain.vert.glsl?raw'
import lightsFrag from './shaders/lights.frag.glsl?raw'
import lightsVert from './shaders/lights.vert.glsl?raw'
import metrosFrag from './shaders/metros.frag.glsl?raw'
import metrosVert from './shaders/metros.vert.glsl?raw'
import pathFrag from './shaders/path.frag.glsl?raw'
import pathVert from './shaders/path.vert.glsl?raw'
import planetFrag from './shaders/planet.frag.glsl?raw'
import planetVert from './shaders/planet.vert.glsl?raw'
import sparksFrag from './shaders/sparks.frag.glsl?raw'
import sparksVert from './shaders/sparks.vert.glsl?raw'
import spriteFrag from './shaders/sprite.frag.glsl?raw'
import spriteVert from './shaders/sprite.vert.glsl?raw'
import townsFrag from './shaders/towns.frag.glsl?raw'
import townsVert from './shaders/towns.vert.glsl?raw'
import vertexHead from './shaders/vertex-head.glsl?raw'

/** A program's vertex and fragment source. */
export type ShaderSource = readonly [vertex: string, fragment: string]

export const SHADERS = {
  /** the planet: the dark disc, the land, the backlight and the rims, per pixel */
  planet: [vertexHead + common + planetVert, fragmentHead + common + camera + planetFrag],
  /** the coastline */
  coast: [vertexHead + common + camera + coastVert, fragmentHead + common + coastFrag],
  /** every town */
  towns: [vertexHead + common + camera + townsVert, fragmentHead + common + townsFrag],
  /** the soft glow round the largest cities */
  metros: [vertexHead + common + camera + metrosVert, fragmentHead + common + metrosFrag],
  /** the settled lights of the globe */
  lights: [vertexHead + common + camera + lightsVert, fragmentHead + common + lightsFrag],
  /** the supernova's particles and their trails */
  sparks: [vertexHead + common + sparksVert, fragmentHead + common + sparksFrag],
  /** film grain */
  grain: [vertexHead + grainVert, fragmentHead + grainFrag],
  /** the 2D stand-in's strokes and fills */
  path: [vertexHead + common + pathVert, fragmentHead + common + pathFrag],
  /** ...its circles and ellipses */
  disc: [vertexHead + common + discVert, fragmentHead + common + discFrag],
  /** ...and its images */
  sprite: [vertexHead + common + spriteVert, fragmentHead + spriteFrag],
} as const satisfies Record<string, ShaderSource>

export type ProgramName = keyof typeof SHADERS
